import { useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { ReactReader } from "react-reader";
import { fetchBriefBankPreviewUrl } from "../../service/briefBank";
import { ReaderBody, ReaderStatus } from "./MyFilesStyled";

/**
 * In-app epub reader for My Files (personal storage only — see
 * fileUtils.isEpubFile). Wraps react-reader (epub.js): it fetches the
 * book's bytes directly from the same presigned URL every other document
 * type already opens through, so no new backend endpoint is needed.
 *
 * `embedded` renders just the reader pane (for MyFiles.js's right-hand
 * viewer panel, alongside ReadOnlyDocumentViewer for other file types) —
 * same convention as BriefBankPreviewModal's `embedded` prop. Without it,
 * this falls back to a standalone modal.
 */
const EpubReader = ({ document, embedded = false, open = false, onClose }) => {
  const [url, setUrl] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const active = embedded || open;

  useEffect(() => {
    if (!active || !document) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setUrl(null);
    setLocation(null);

    (async () => {
      try {
        const response = await fetchBriefBankPreviewUrl(document.id);
        const fetchedUrl =
          response?.data?.url || response?.data?.presigned_url || response?.data;
        if (cancelled) return;
        if (typeof fetchedUrl === "string" && fetchedUrl) {
          setUrl(fetchedUrl);
        } else {
          setError("Could not load this book.");
        }
      } catch {
        if (!cancelled) setError("Could not load this book. Try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, document?.id]);

  const body = (
    <ReaderBody $embedded={embedded}>
      {loading && (
        <ReaderStatus>
          <Spin /> Loading book…
        </ReaderStatus>
      )}
      {!loading && error && <ReaderStatus>{error}</ReaderStatus>}
      {!loading && !error && url && (
        <ReactReader url={url} location={location} locationChanged={setLocation} showToc />
      )}
    </ReaderBody>
  );

  if (embedded) return body;

  return (
    <Modal
      title={document?.name || "Reader"}
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(900px, calc(100vw - 48px))"
      styles={{ body: { padding: 0 } }}
      destroyOnHidden
    >
      {body}
    </Modal>
  );
};

export default EpubReader;
